// converter for default channels availability for each product on listing page
import { getDefaultChannelsAvailabilityTranslation } from '../translations/defaultChannelsAvailabilitiesTranslations';
import UISettings from '../settings/UISettings';
import UILocale from '../translations/UILocale';


export function getDefaultDeliveriesAndCollectionForProduct(productData) {
  if (productData.boxType === 'download') {
    return {
      canBeDownloaded: true
    };
  }

  const delivery = getDeliveriesInfoForProduct(productData);
  const inStore = getCollectionInfoForProduct(productData);

  if (delivery.type === 'homeDeliveryUnavailable' && inStore.type === 'collectInStoreUnavailable') {
    // product is not deliverable and collectable
    if (isProductSupportShowEmailMeBack(productData)) {
      // supports show email me back
      return {
        showEmailMeBack: true
      };
    }

    // if product is not deliverable and collectable and doesn't support show email me back,
    // we change the label of inStore (the second label) to 'out of stock'
    return {
      delivery,
      inStore: {
        ...inStore,
        label: 'Sorry this item is out of stock' // TODO: Add to translations
      }
    };
  }

  return {
    delivery,
    inStore
  };
}

// returns if there can be displayed show email me back button in case of out of stock of the product
export function isProductSupportShowEmailMeBack(productData) {
  // global switch for emailmeback button
  // by default is set to true if it's not present in JSON/LD settings
  const isEmailMeBackEnabledGlobally = UISettings.get('component.emailbackform.oos_email_active') !== undefined
    ? UISettings.get('component.emailbackform.oos_email_active') : true;

  // segment switch for emailmeback button
  // by default is set to true if it's not present in JSON/LD settings
  const isEmailMeBackEnabledForSegment = UISettings.get('component.emailbackform.active_for_this_segment') !== undefined
    ? UISettings.get('component.emailbackform.active_for_this_segment') : true;

  if (!isEmailMeBackEnabledGlobally || !isEmailMeBackEnabledForSegment) {
    return false;
  }

  //  email me back can't be be shown for products with 'Clearance' or 'clearance' tags
  if (productData.clearance) {
    return false;
  }

  // email me back can't be shown for product which is preorder or forward order
  if (productData.forwardOrder.available || productData.preOrder.available) {
    return false;
  }

  // email me back can't be shown for product of type different from bigbox, smallbox or sbre
  if (productData.boxType !== 'small-box' && productData.boxType !== 'big-box' &&
    productData.boxType !== 'sbre') {
    return false;
  }


  // email me back can't be shown for product with price ending on .91 or .97
  const priceSuffix = productData.price.currentPrice.split('.')[1];
  if (priceSuffix === '91' || priceSuffix === '97') {
    return false;
  }

  return true;
}

function getDeliveriesInfoForProduct(productData) {
  // if product is forward order, it's deliverable
  if (productData.forwardOrder.available) {
    return {
      type: 'homeDeliveryAvailable',
      // shd has different translation than other product types for forward order
      label: productData.boxType === 'shd'
        ? getDefaultChannelsAvailabilityTranslation('shd', 'deliverable')
        : `${getDefaultChannelsAvailabilityTranslation('forwardOrder', 'deliverable')} ` +
      `${productData.forwardOrder.message}`
    };
  }

  // if product is pre order, it's deliverable
  if (productData.preOrder.available) {
    return {
      type: 'homeDeliveryAvailable',
      label: `${getDefaultChannelsAvailabilityTranslation('preOrder', 'deliverable')} ${productData.preOrder.message}`
    };
  }

  // if product supports home delivery, so it's requires more logic to check its deliverability
  if (productData.delivery.isHomeDelivery) {
    // it's necessary to check the stock of product to determine deliverability
    if (productData.checkInStock) {
      if (productData.stockChecked) {
        // stock has been checked
        return productData.inStock
          ? { // if product is in stock, it's deliverable
            type: 'homeDeliveryAvailable',
            label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'deliverable')
          }
          : { // if product is not in stock, it's not deliverable
            type: 'homeDeliveryUnavailable',
            label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'undeliverableForbidden')
          };
      }

      // stock hasn't been checked yet
      return {
        type: 'checkInStore',
        label: 'Checking stock...' // TODO: add to translations
      };
    }

    // Because it's not necessary to check stock, the product is deliverable
    return {
      type: 'homeDeliveryAvailable',
      label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'deliverable')
    };
  }

  // // in all other cases, product is not deliverable
  return {
    type: 'homeDeliveryUnavailable',
    label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'undeliverableForbidden')
  };
}

function getCollectionInfoForProduct(productData) {
  if (!isCollectionGloballyTurnedOn()) {
    // if collection is globally turned off by settings in site management, the product is not collectable
    return {
      type: 'collectInStoreUnavailable',
      label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'uncollectable')
    };
  }

  // if product is forwardOrder, it's not collectable
  if (productData.forwardOrder.available) {
    return {
      type: 'collectInStoreUnavailable',
      // shd has different translation than other product types for forward order
      label: productData.boxType === 'shd'
        ? getDefaultChannelsAvailabilityTranslation('shd', 'uncollectable')
        : getDefaultChannelsAvailabilityTranslation('forwardOrder', 'uncollectable')
    };
  }

  // if product is preOrder, it's not collectable
  if (productData.preOrder.available) {
    return {
      type: 'collectInStoreUnavailable',
      label: getDefaultChannelsAvailabilityTranslation('preOrder', 'uncollectable')
    };
  }

  // if product is ReserveAndCollect, Contactless PayAndCollect, it's collectable
  if (isProductReserveAndCollect(productData) || isProductContactlessPayAndCollect(productData)) {
    return {
      type: 'collectInStoreAvailable',
      label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'collectable')
    };
  }

  // if product supports PayAndCollect, the logic is more complex
  if (isProductPayAndCollect(productData)) {
    if (productData.checkInStock) {
      // it's necessary to check the stock of product to determine collectability
      if (productData.stockChecked) {
        // stock has been already checked
        return productData.inStock
          ? { // if product is in stock, it's collectable
            type: 'collectInStoreAvailable',
            label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'collectable')
          }
          : { // if product is not in stock, it's uncollectable
            type: 'collectInStoreUnavailable',
            label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'uncollectable')
          };
      }

      // stock hasn't been checked yet
      return {
        type: 'checkInStore',
        label: 'Checking stock...' // TODO: add to translations
      };
    }

    // Because it's not necessary to check stock, the product is collectable
    return {
      type: 'collectInStoreAvailable',
      label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'collectable')
    };
  }

  // in all other cases, product is not collectable
  return {
    type: 'collectInStoreUnavailable',
    label: getDefaultChannelsAvailabilityTranslation(productData.boxType, 'uncollectable')
  };
}

// check if collection is globally turned on based on settings in site management
function isCollectionGloballyTurnedOn() {
  // by default is set to true if it's not present in JSON/LD settings
  return UISettings.get('component.mcd.store_collection_active_status') !== undefined
    ? UISettings.get('component.mcd.store_collection_active_status') : true;
}

function isProductReserveAndCollect(productData) {
  // global switch ror ReserveAndCollect
  // by default is set to true if it's not present in JSON/LD settings
  const isReserveAndCollectActive = UISettings.get('component.mcd.reserve_and_collect_active_status') !== undefined
    ? UISettings.get('component.mcd.reserve_and_collect_active_status') : true;

  return productData.delivery.isReserveAndCollect && isReserveAndCollectActive;
}

function isProductPayAndCollect(productData) {
  // global switch for PayAndCollect
  // by default is set to true if it's not present in JSON/LD settings
  const isPayAndCollectActive = UISettings.get('component.mcd.pay_and_collect_active_status') !== undefined
    ? UISettings.get('component.mcd.pay_and_collect_active_status') : true;

  // only small-box and sbre product types support PayAndCollect
  return (productData.boxType === 'small-box' || productData.boxType === 'sbre') &&
    productData.delivery.isPayAndCollect && isPayAndCollectActive;
}

function isProductContactlessPayAndCollect(productData) {
  // global switch for Contactless PayAndCollect
  // by default is set to true if it's not present in JSON/LD settings
  const isContactlessPayAndCollectActive = UISettings.get('component.mcd.pay_and_collect_store_stock_active_status') !== undefined
    ? UISettings.get('component.mcd.pay_and_collect_store_stock_active_status') : true;

  // sameDayDelivery flag in BRAIN means Contactless PayAndCollect
  return productData.delivery.isSameDayDelivery && isContactlessPayAndCollectActive;
}


/*  Gathering delivery and collection for sponsored product */
export function convertDefaultChannelsAvailabilityForSponsoredProduct(deliveryOptions) {
  if (deliveryOptions) {
    const delivery = UILocale.getString('component.hooklogic.delivery');
    const inStore = UILocale.getString('component.hooklogic.inStore');
    return {
      showEmailMeBack: false,
      canBeDownloaded: isCanBeDownloaded(getDeliveryOption(22, deliveryOptions)),
      delivery: convertDelivery(getDeliveryOption(1, deliveryOptions,
        delivery === 'delivery' ? null : delivery)),
      inStore: convertDelivery(getDeliveryOption(20, deliveryOptions,
        inStore === 'in_store' ? null : inStore))
    };
  }

  return undefined;
}

function convertDelivery(deliveryOption) {
  const deliveryType = deliveryOption.id && getDeliveryType(deliveryOption.id);
  return deliveryOption && deliveryOption.enabled
    ? {
      type: deliveryType,
      label: deliveryOption.label
    }
    : undefined;
}

function getDeliveryType(id) {
  switch (id) {
    case 1:
      return 'homeDeliveryAvailable';
    case 20:
      return 'collectInStoreAvailable';
    default:
      return null;
  }
}

function isCanBeDownloaded(deliveryOption) {
  return deliveryOption && deliveryOption.enabled;
}

function getDeliveryOption(id, deliveryOptions, translateLabel = null) {
  const deliveryOption = deliveryOptions.find((item) => Number(item.id) === id);
  if (deliveryOption) {
    return {
      ...deliveryOption,
      id: Number(deliveryOption.id),
      label: translateLabel || deliveryOption.label
    };
  }

  return deliveryOption;
}
